import { useServicesQuery } from '@/services/api';
import type { ServicesApiGetEnvHealthQueryParams } from '@/services/api/services-api';
import { useEnvHealthCheck } from './get-env-health-check';
import { ENV_HEALTH_REFETCH_INTERVAL_ACTIVE } from '../constants';

const REFETCH_INTERVAL_FLOOR_MS = 5_000;

/**
 * Schedule the next active refetch to land just after the backend cache
 * refreshes. Falls back to the constant ceiling when no hint is available
 * (e.g. before the first response, or during the test mocks).
 */
export function getActiveRefetchInterval(data: { next_refresh_at?: number } | undefined, now = Date.now()): number {
  if (data?.next_refresh_at == null) return ENV_HEALTH_REFETCH_INTERVAL_ACTIVE;
  const msUntilRefresh = data.next_refresh_at * 1000 - now;
  return Math.max(REFETCH_INTERVAL_FLOOR_MS, Math.min(ENV_HEALTH_REFETCH_INTERVAL_ACTIVE, msUntilRefresh));
}

/**
 * Fetches workspace health.
 *
 * When `active` is true (user is on the workspace-health dashboard):
 *   - Heavy /health endpoint polls every 5 min, aligned to the backend cache
 *     refresh via the response's `next_refresh_at` hint when available.
 *
 * When `active` is false (off-page: nav widget, home banner):
 *   - Subscribes to the lightweight /health/check endpoint (60s interval).
 *   - Heavy /health is enabled only while the light check reports unhealthy;
 *     react-query toggles fetching automatically as `enabled` flips.
 *   - On recovery, the heavy query is disabled but its cached data may be
 *     stale. Off-page consumers should gate visibility on the returned
 *     `isUnhealthy` flag (driven by the fresh light signal), not on
 *     `data.health` directly.
 */
export function useEnvHealth(
  workspaceId: number,
  simulate: ServicesApiGetEnvHealthQueryParams['simulate'],
  active = false,
) {
  const lightCheck = useEnvHealthCheck(workspaceId, !active);

  const heavy = useServicesQuery(
    'getEnvHealth',
    { params: { environment_id: workspaceId }, query: { simulate } },
    {
      refetchInterval: active ? (query) => getActiveRefetchInterval(query.state.data) : false,
      enabled: workspaceId > 0 && (active || lightCheck.data?.healthy === false),
      refetchOnMount: active ? 'always' : false,
      refetchOnWindowFocus: active ? 'always' : false,
    },
  );

  // Off-page: trust the fresh light signal for visibility — heavy.data may be
  // stale from a prior unhealthy window. On-page: rely on the live heavy data.
  // In both cases require heavy.data to be present so consumers can render
  // unhealthy visuals without further null-guards; during the loading window
  // (light flipped unhealthy, heavy fetch in flight) we render as healthy and
  // flip to unhealthy in a single tick once data arrives.
  const isUnhealthy =
    heavy.data !== undefined && (active ? heavy.data.health !== 'healthy' : lightCheck.data?.healthy === false);

  return { ...heavy, isUnhealthy };
}
